import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { transportesAPI } from '../../services/api';

// Estado inicial
const initialState = {
  transportes: [],
  loading: false,
  error: null,
  filters: {
    search: '',
    tipo: 'all' // all, nacional, exportacion
  }
};

// Thunks asíncronos
export const fetchTransportes = createAsyncThunk(
  'transportes/fetchTransportes',
  async (filters, { rejectWithValue }) => {
    try {
      const response = await transportesAPI.getAll(filters);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const createTransporte = createAsyncThunk(
  'transportes/createTransporte',
  async (transporteData, { rejectWithValue }) => {
    try {
      const response = await transportesAPI.create(transporteData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateTransporte = createAsyncThunk(
  'transportes/updateTransporte',
  async ({ id, transporteData }, { rejectWithValue }) => {
    try {
      const response = await transportesAPI.update(id, transporteData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const deleteTransporte = createAsyncThunk(
  'transportes/deleteTransporte',
  async (id, { rejectWithValue }) => {
    try {
      await transportesAPI.delete(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Slice
const transportesSlice = createSlice({
  name: 'transportes',
  initialState,
  reducers: {
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = initialState.filters;
    },
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch transportes
      .addCase(fetchTransportes.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTransportes.fulfilled, (state, action) => {
        state.loading = false;
        state.transportes = action.payload;
      })
      .addCase(fetchTransportes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create transporte
      .addCase(createTransporte.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createTransporte.fulfilled, (state, action) => {
        state.loading = false;
        state.transportes.push(action.payload);
      })
      .addCase(createTransporte.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update transporte
      .addCase(updateTransporte.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateTransporte.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.transportes.findIndex(t => t.id === action.payload.id);
        if (index !== -1) {
          state.transportes[index] = action.payload;
        }
      })
      .addCase(updateTransporte.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete transporte
      .addCase(deleteTransporte.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteTransporte.fulfilled, (state, action) => {
        state.loading = false;
        state.transportes = state.transportes.filter(t => t.id !== action.payload);
      })
      .addCase(deleteTransporte.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

// Selectores
export const selectAllTransportes = (state) => state.transportes.transportes;
export const selectTransportesLoading = (state) => state.transportes.loading;
export const selectTransportesError = (state) => state.transportes.error;
export const selectTransportesFilters = (state) => state.transportes.filters;

export const selectFilteredTransportes = (state) => {
  const { transportes, filters } = state.transportes;
  
  return transportes.filter(transporte => {
    // Filtro por búsqueda
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      if (!transporte.nombre.toLowerCase().includes(searchLower)) {
        return false;
      }
    }
    
    // Filtro por tipo
    if (filters.tipo && filters.tipo !== 'all') {
      if (transporte.tipo !== filters.tipo) {
        return false;
      }
    }
    
    return true;
  });
};

// Exportar acciones y reducer
export const { setFilters, clearFilters, clearError } = transportesSlice.actions;
export default transportesSlice.reducer;
